import { exportTimesheet } from "@/time-tracking/api";
import { exportTimesheetToXlsx } from "@/time-tracking/utils/timesheetExport";
import { Alert, DatePicker, message, Modal, Space } from "antd";
import dayjs, { Dayjs } from "dayjs";

import { useEffect, useState } from "react";

const { RangePicker } = DatePicker;

interface ExportDateRangeModalProps {
  selectedUserIds: number[];
  open: boolean;
  onClose: () => void;
}

const ExportDateRangeModal = ({
  selectedUserIds,
  open,
  onClose,
}: ExportDateRangeModalProps) => {
  const getDefaultDateRange = (): [Dayjs, Dayjs] => {
    const today = dayjs();
    // Return the most recent full week plus previous week (Thursday to Wednesday)
    const lastWednesday = today.day() >= 3 ? today.day(3) : today.day(-4);
    const lastThursday = lastWednesday.subtract(13, "day");
    return [lastThursday as Dayjs, lastWednesday as Dayjs];
  };

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(
    getDefaultDateRange()
  );

  const rangeOver4Weeks = dateRange[1].diff(dateRange[0], "week") >= 4;

  useEffect(() => {
    if (open) {
      setDateRange(getDefaultDateRange());
    }
  }, [open]);

  const handleExport = async () => {
    try {
      const result = await exportTimesheet({
        userIds: selectedUserIds,
        from: dateRange[0].format("YYYY-MM-DD"),
        to: dateRange[1].format("YYYY-MM-DD"),
      });

      if (result.status === 200) {
        const { data } = result.data;

        await exportTimesheetToXlsx({
          startDate: data.from,
          endDate: data.to,
          employeeTimesheetData: data.result,
        });

        message.success("Timesheet successfully exported");
      } else {
        throw new Error(`Export failed with status ${result.status}`);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Export error:", error);
      message.error("Failed to export timesheet. Please try again.");
    } finally {
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      closeIcon={null}
      onCancel={onClose}
      maskClosable={false}
      okText="Export"
      onOk={handleExport}
      okButtonProps={{
        disabled: rangeOver4Weeks,
      }}
      centered
      title="Select date range for export"
    >
      <Space
        direction="vertical"
        style={{
          width: "100%",
        }}
      >
        {rangeOver4Weeks && (
          <Alert
            message="Maximum export range is 4 weeks"
            type="error"
            showIcon
          />
        )}
        <RangePicker
          style={{
            width: "100%",
          }}
          allowClear={false}
          value={dateRange}
          onChange={(dates) => {
            if (dates) {
              setDateRange([dates[0] as Dayjs, dates[1] as Dayjs]);
            }
          }}
        />
        <p>Please note, that regular pay periods are Thursday to Wednesday.</p>
      </Space>
    </Modal>
  );
};

export default ExportDateRangeModal;
